import { provinces } from "@/data/provinces";
import React, { lazy, Suspense } from "react";
import { useParams, Navigate } from "react-router-dom";
import { isNeighborhoodSlug } from "@/data/neighborhood-slugs";

/**
 * Single-segment URL dispatcher.
 *
 * React Router v7 does NOT support inline path params like `prefix-:slug`,
 * so we register one catch-all `/:fullSlug` route in App.js and parse the
 * prefix here. This wrapper currently dispatches to:
 *
 *   /ricerca-perdite-acqua-{citySlug}-{nbSlug}    → NeighborhoodPage (if known)
 *   /ricerca-perdite-acqua-{slug}                 → ComunePage(slug)
 *   /perdite-bagno-{slug}                         → ServiceComunePage(perdite-bagno, slug)
 *   /perdite-tetto-{slug}                         → ServiceComunePage(perdite-tetto, slug)
 *   /perdite-condominio-{slug}                    → ServiceComunePage(perdite-condominio, slug)
 *   /pronto-intervento-perdite-{slug}             → ServiceComunePage(pronto-intervento-perdite, slug)
 *   /termocamera-perdite-{slug}                   → ServiceComunePage(termocamera-perdite, slug)
 *   /perdite-piscina-{slug}                       → ServiceComunePage(perdite-piscina, slug)
 *   anything else                                  → NotFound
 *
 * IMPORTANT: longer prefixes MUST be tested BEFORE shorter ones — e.g.
 *   "pronto-intervento-perdite-firenze" must NOT match "perdite-" as prefix.
 * The SERVICE_PREFIXES array below is sorted by length descending precisely
 * for this reason.
 *
 * NEIGHBORHOOD MATCHING: data-driven via NEIGHBORHOODS map (see
 * data/neighborhoods.js). Tested BEFORE the comune slug match so that
 * "pisa-cisanello" routes to NeighborhoodPage instead of being treated
 * as an unknown comune slug.
 */
const ComunePage = lazy(() => import("@/pages/ComunePage"));
const NeighborhoodPage = lazy(() => import("@/pages/NeighborhoodPage"));
const ServiceComunePage = lazy(() => import("@/pages/ServiceComunePage"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const SEO_PREFIX = "ricerca-perdite-acqua-";

// Order matters: longest prefix wins. Each item ends with "-" so we don't
// accidentally match `perdite-bagno-condominio-x` against `perdite-bagno-`.
const SERVICE_PREFIXES = [
  "pronto-intervento-perdite-",
  "termocamera-perdite-",
  "perdite-condominio-",
  "perdite-piscina-",
  "perdite-bagno-",
  "perdite-tetto-",
];

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="loading-screen">
      <div className="h-10 w-10 rounded-full border-2 border-[hsl(var(--brand-aqua))] border-t-transparent animate-spin" />
    </div>
  );
}

const PROVINCE_SLUGS = new Set(provinces.map((p) => p.slug));
function isProvinceSlug(slug) {
  return PROVINCE_SLUGS.has(slug);
}

export default function ComuneSeoRouter() {
  const { fullSlug = "" } = useParams();

  // 1) Existing /ricerca-perdite-acqua-{slug}
  //    A) If `slug` matches a known neighborhood (e.g. "pisa-cisanello")
  //       → NeighborhoodPage (tested first to avoid false-positive comune match).
  //    B) Otherwise → ComunePage(slug)
  if (fullSlug.startsWith(SEO_PREFIX)) {
    const realSlug = fullSlug.slice(SEO_PREFIX.length);
    if (!realSlug) return <Navigate to="/" replace />;
    if (isNeighborhoodSlug(realSlug)) {
      return (
        <Suspense fallback={<LoadingFallback />}>
          <NeighborhoodPage slugOverride={realSlug} />
        </Suspense>
      );
    }
    return (
      <Suspense fallback={<LoadingFallback />}>
        <ComunePage slugOverride={realSlug} />
      </Suspense>
    );
  }

  // 2) Programmatic SEO service × comune pages
  for (const prefix of SERVICE_PREFIXES) {
    if (fullSlug.startsWith(prefix)) {
      const comuneSlug = fullSlug.slice(prefix.length);
      if (!comuneSlug) return <Navigate to="/" replace />;
      const serviceSlug = prefix.slice(0, -1); // strip trailing "-"
      return (
        <Suspense fallback={<LoadingFallback />}>
          <ServiceComunePage serviceSlugOverride={serviceSlug} comuneSlugOverride={comuneSlug} />
        </Suspense>
      );
    }
  }

  // 3) Legacy province URLs (/firenze, /pisa, ...) listed in an old sitemap:
  //    the canonical page is /zone/<slug>. Client-side redirect so Google and
  //    visitors land on the right page instead of a 404.
  if (isProvinceSlug(fullSlug)) {
    return <Navigate to={`/zone/${fullSlug}`} replace />;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <NotFound />
    </Suspense>
  );
}
